
import { computed, defineComponent, PropType } from "vue";
import EditableItemNumber from "@/components/editable-items/EditableItemNumber/EditableItemNumber.vue";
import EditableItemEnum from "@/components/editable-items/EditableItemEnum/EditableItemEnum.vue";
import RemoveButton from "@/components/buttons/RemoveButton/RemoveButton.vue";
import { SchoolType, schoolTypes } from "@/ts/objects/value/school-type";
import { Class } from "@/ts/objects/entity/class";
import { isNullish, parseIntOrNull } from "@/ts/utils/common-util";
import { useAsyncComputed } from "@/composables/use-async-computed";
import {
  gradeNumbersOfSchoolType,
  isElementaryGradeNumber,
  isJuniorhighGradeNumber,
} from "@/ts/objects/value/grade-number";
import EditableItemText from "@/components/editable-items/EditableItemText/EditableItemText.vue";
import { names } from "@/ts/app/object-name";

const CLASS_UNSELECTED = "__CLASS_UNSELECTED__";

export default defineComponent({
  name: "TeacherClassesSelectPopupItem",
  components: {
    EditableItemText,
    RemoveButton,
    EditableItemEnum,
    EditableItemNumber,
  },
  props: {
    width: { type: Number, default: 260 },
    height: { type: Number, default: 42 },

    /**
     * 選択中の年度。
     */
    schoolYear: { type: Number, default: null },
    /**
     * 選択中の学校タイプ。
     */
    schoolType: { type: String as PropType<SchoolType>, required: true },
    /**
     * 選択中の学年。
     */
    gradeNumber: { type: Number, default: 1 },
    /**
     * 選択中のクラスID。
     */
    classId: { type: String, default: null },
    /**
     * 選択中の担任タイプ。
     */
    inChargeType: { type: String, required: true },

    /**
     * ある年度のすべてのクラスを、正しい並び順で取得する関数。
     */
    getClasses: {
      type: Function as PropType<(schoolYear: number) => Promise<Class[]>>,
      required: true,
    },

    /**
     * 学校タイプ・学年・クラスが変更されたときに呼び出される。
     */
    onChange: {
      type: Function as PropType<
        (
          schoolYear: number | null,
          schoolType: SchoolType,
          gradeNumber: number | null,
          cls: Class | null,
          inChargeType: string,
        ) => void
      >,
      required: true,
    },
    /**
     * 削除ボタンを押したときに呼び出される。
     */
    onRemove: { type: Function as PropType<() => void>, required: true },
  },
  setup(props) {
    const update = async (
      schoolYearInput: number | null,
      schoolTypeInput: SchoolType,
      gradeNumberInput: number,
      classIdInput: string | null,
      inChargeTypeInput: string,
    ) => {
      const [schoolType, gradeNumber] = sanitize(
        schoolTypeInput,
        gradeNumberInput,
      );

      if (
        isNullish(schoolYearInput) ||
        isNullish(gradeNumber) ||
        isNullish(classIdInput)
      ) {
        props.onChange(
          schoolYearInput,
          schoolType,
          gradeNumber,
          null,
          inChargeTypeInput,
        );
        return;
      }

      const classes = await props.getClasses(schoolYearInput);
      const cls = classes.find((c) => c.classId === classIdInput);
      if (
        isNullish(cls) ||
        cls.schoolYear !== schoolYearInput ||
        cls.grade.schoolType !== schoolType ||
        cls.grade.gradeNumber !== gradeNumber
      ) {
        props.onChange(
          schoolYearInput,
          schoolType,
          gradeNumber,
          null,
          inChargeTypeInput,
        );
        return;
      }

      props.onChange(
        schoolYearInput,
        schoolType,
        gradeNumber,
        cls,
        inChargeTypeInput,
      );
    };

    const [selectableClasses] = useAsyncComputed(async () => {
      const schoolYear = props.schoolYear;
      const schoolType = props.schoolType;
      const gradeNumber = props.gradeNumber;
      if (
        isNullish(schoolYear) ||
        isNullish(schoolType) ||
        isNullish(gradeNumber)
      )
        return [];

      return (await props.getClasses(schoolYear)).filter(
        (c) =>
          c.grade.gradeNumber === gradeNumber &&
          c.grade.schoolType === schoolType,
      );
    }, []);
    const classOptions = computed(() => [
      { value: CLASS_UNSELECTED, name: "－" },
      ...selectableClasses.value.map((c) => ({
        value: c.classId,
        name: c.name,
      })),
    ]);
    const classInputEnabled = computed(
      () => selectableClasses.value.length > 0,
    );
    const classIdSelectValue = computed(() => {
      if (isNullish(props.classId)) return CLASS_UNSELECTED;
      return props.classId;
    });

    const schoolYearLabel = names.schoolYear.d;
    const schoolTypeLabel = names.schoolType.d;
    const gradeNumberLabel = names.gradeNumber.d;
    const classNameLabel = names.className.d;
    const inChargeTypeLabel = names.inChargeType.d;

    return {
      schoolTypeOptions: Object.entries(schoolTypes).map(([k, v]) => ({
        name: v,
        value: k,
      })),

      gradeNumberSelectValue: computed(() => props.gradeNumber.toString(10)),
      gradeNumberOptions: computed(() =>
        gradeNumbersOfSchoolType[props.schoolType].map((gradeNumber) => ({
          name: gradeNumber.toString(10),
          value: gradeNumber.toString(10),
        })),
      ),

      message: computed(() => {
        if (
          isNullish(props.schoolYear) ||
          isNullish(props.schoolType) ||
          isNullish(props.gradeNumber) ||
          isNullish(props.classId)
        )
          return "年度とクラスを入力してください。";
        return null;
      }),

      classOptions,
      classInputEnabled,
      classIdSelectValue,

      onInputSchoolYearInternal: (value: number | null) => {
        update(
          value,
          props.schoolType,
          props.gradeNumber,
          props.classId,
          props.inChargeType,
        );
      },
      onInputSchoolTypeInternal: (value: SchoolType) => {
        update(
          props.schoolYear,
          value,
          props.gradeNumber,
          props.classId,
          props.inChargeType,
        );
      },
      onInputGradeNumberInternal: (value: string) => {
        const intValue = parseIntOrNull(value) ?? 1;
        update(
          props.schoolYear,
          props.schoolType,
          intValue,
          props.classId,
          props.inChargeType,
        );
      },
      onInputClassInternal: (value: string) => {
        update(
          props.schoolYear,
          props.schoolType,
          props.gradeNumber,
          value === CLASS_UNSELECTED ? null : value,
          props.inChargeType,
        );
      },
      onInputInChargeTypeInternal: (value: string) => {
        update(
          props.schoolYear,
          props.schoolType,
          props.gradeNumber,
          props.classId,
          value,
        );
      },

      schoolYearLabel,
      schoolTypeLabel,
      gradeNumberLabel,
      classNameLabel,
      inChargeTypeLabel,

      styles: {
        "--width": `${props.width}px`,
        "--height": `${props.height}px`,
      },
    };
  },
});

function sanitize(
  schoolType: SchoolType,
  gradeNumber: number,
): [SchoolType, number] {
  if (schoolType === "elementary") {
    if (isElementaryGradeNumber(gradeNumber))
      return ["elementary", gradeNumber];
    return ["elementary", 1];
  } else {
    if (isJuniorhighGradeNumber(gradeNumber))
      return ["juniorhigh", gradeNumber];
    return ["juniorhigh", 1];
  }
}
